<script>
import { Helper } from '@/mixin/helper'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header'
import { GET_VEHICLE_ID, UPDATE_VEHICLES } from '@/services/api/master/fleet'
import McButton from '@/components/McButton'

export default {
  components: {
    Layout,
    McButton,
    PageHeader,
  },
  mixins: [Helper],
  apollo: {
    vehicle: {
      fetchPolicy: 'no-cache',
      query: GET_VEHICLE_ID,
      variables() {
        return {
          filter: {
            id: Number(this.$route.params.vehicleId),
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const result = data.vehicle.vehicle
          this.data.licensePlate = result.licensePlate
          const vgroups = result.vehicle2Vgroup
          const nameGroups = vgroups.map((item) => item.vgroup.name)
          this.data.vgroups = nameGroups.join(', ')
          this.data.fleetCategory = result.vehicleType.category
          this.data.fleetType = result.vehicleType.type
          this.form.idleTimeLimit = result.idleDuration ?? 15
          this.form.parkingTimeLimit = result.parkingDuration ?? 1440
          this.data.vehicleId = result.id ?? 0
          this.current.idleTimeLimit = result.idleDuration ?? 0
          this.current.parkingTimeLimit = result.parkingDuration ?? 0
        }
      },
    },
  },
  props: {
    status: String,
  },
  data() {
    return {
      data: {
        vehicleId: 0,
        licensePlate: '',
        vgroups: '',
        fleetCategory: '',
        fleetType: '',
      },
      form: {
        idleTimeLimit: 15,
        parkingTimeLimit: 1440,
      },
      current: {
        idleTimeLimit: 0,
        parkingTimeLimit: 0,
      },
      isUpdated: false,
    }
  },
  computed: {
    idleTimeLimitState() {
      return this.form.idleTimeLimit >= 5 && this.form.idleTimeLimit <= 10080
    },
    parkingTimeLimitState() {
      return this.form.parkingTimeLimit >= 5 && this.form.parkingTimeLimit <= 10080
    },
    isValidForm() {
      return (
        this.idleTimeLimitState &&
        this.parkingTimeLimitState &&
        (this.form.idleTimeLimit != this.current.idleTimeLimit ||
          this.form.parkingTimeLimit != this.current.parkingTimeLimit)
      )
    },
    title() {
      return this.$t('global.changeParameter')
    },
    items() {
      return [
        {
          text: this.$t('setting.homePage.pengaturan'),
          to: { name: 'Setting' },
        },
        {
          text: this.$t('setting.list.trackerSensor.trackerSensor'),
          to: { name: 'Setting Tracker & Sensor' },
        },
        {
          text: this.$t('setting.list.trackerSensor.idleParking'),
          to: { name: 'Idle & Parking Limit Times' },
        },
        {
          text: this.$t('global.changeParameter'),
          active: true,
        },
      ]
    },
    /**
     * Management Field for b-table
     */
  },
  methods: {
    submitForm() {
      this.$bvModal.show('mc-modal-confirm')
    },

    async confirmForm() {
      this.isUpdated = true
      this.$bvModal.hide('mc-modal-confirm')
      const input = {
        id: [this.data.vehicleId],
        newData: {
          idleDuration: Number(this.form.idleTimeLimit),
          parkingDuration: Number(this.form.parkingTimeLimit),
        },
      }
      const res = await this.$apollo.mutate({
        mutation: UPDATE_VEHICLES,
        variables: {
          input: input,
        },
      })
      const message = res.data.updateVehicles.message
      this.isUpdated = false
      this.current.idleTimeLimit = this.form.idleTimeLimit
      this.current.parkingTimeLimit = this.form.parkingTimeLimit
      if (message == 'Successful') {
        this.$mcToast.success(this.$t('global.alert.text.changed'))
      } else {
        this.$mcToast.error(this.$t('global.alert.text.failChange'))
      }
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" class="mc-page-three" />
    <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
      <div class="float-end">
        <McButton
          variant="secondary"
          class="mx-2"
          :text="$t('global.back')"
          icon="icon-outline_arrow-left"
          @click="$router.push({ name: 'Idle & Parking Limit Times' })"
        />
      </div>
    </div>
    <h4 class="card-title">{{ $t('setting.list.trackerSensor.fleetInformation') }}</h4>
    <b-card class="mt-3 mb-5">
      <b-row class="mc-list-info__top">
        <b-col>
          <span>{{ $t('setting.list.trackerSensor.licensePlate') }}</span>
          {{ data.licensePlate ?? '-' }}
        </b-col>
        <b-col>
          <span>{{ $t('setting.list.trackerSensor.vehicleGroup') }}</span>
          {{ data.vgroups ?? '-' }}
        </b-col>
        <b-col>
          <span>{{ $t('setting.list.trackerSensor.vehicleCategory') }}</span>
          {{ data.fleetCategory ?? '-' }}
        </b-col>
        <b-col>
          <span>{{ $t('setting.list.trackerSensor.vehicleType') }}</span>
          {{ data.fleetType ?? '-' }}
        </b-col>
      </b-row>
    </b-card>

    <div class="clearfix mb-3">
      <div class="float-start">
        <h4 class="card-title">{{ $t('setting.list.trackerSensor.timeLimitSetting') }}</h4>
      </div>

      <div class="float-end">
        <McButton
          variant="secondary"
          class="mx-2"
          :text="$t('global.cancel')"
          icon="icon-outline_close"
          @click="$router.push({ name: 'Idle & Parking Limit Times' })"
        />
        <McButton
          variant="primary"
          class="me-2"
          :disabled="isUpdated || !isValidForm"
          :text="$t('global.save')"
          icon="icon-outline_save"
          @click="submitForm"
        >
          <template #before-slot>
            <b-spinner v-if="isUpdated" small label="Small Spinner" />
          </template>
        </McButton>
      </div>
    </div>
    <b-card class="my-3">
      <FormulateForm v-model="form">
        <b-row>
          <b-col sm="12" md="6">
            <FormulateInput
              id="idleTimeLimit"
              name="idleTimeLimit"
              :placeholder="$t('setting.list.trackerSensor.idleTimeLimit')"
              suffix
              validation="^required|min:5|max:10080|number"
              :validation-messages="{
                min: $t('setting.list.trackerSensor.validator.time'),
                max: $t('setting.list.trackerSensor.validator.time'),
              }"
              class="my-3"
              validation-behavior="live"
              error-behavior="live"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label class="formulate-input-label mb-1">
                    {{ $t('setting.list.trackerSensor.idleTimeLimit') }}
                  </label>
                  <a v-b-tooltip.topleft :title="`${$t('setting.list.trackerSensor.minIdleTime')}`">
                    <i class="icon-outline_info" />
                  </a>
                </div>
              </template>
              <template #suffix>
                <div class="formulate-input-suffix">
                  <span>{{ $t('global.time.minutes') }}</span>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col sm="12" md="6">
            <FormulateInput
              id="parkingTimeLimit"
              name="parkingTimeLimit"
              :placeholder="$t('setting.list.trackerSensor.parkingTimeLimit')"
              suffix
              validation="^required|min:5|max:10080|number"
              :validation-messages="{
                min: $t('setting.list.trackerSensor.validator.time'),
                max: $t('setting.list.trackerSensor.validator.time'),
              }"
              class="my-3"
              validation-behavior="live"
              error-behavior="live"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label class="formulate-input-label mb-1">
                    {{ $t('setting.list.trackerSensor.parkingTimeLimit') }}
                  </label>
                  <a
                    v-b-tooltip.topleft
                    :title="`${$t('setting.list.trackerSensor.minParkingTime')}`"
                  >
                    <i class="icon-outline_info" />
                  </a>
                </div>
              </template>
              <template #suffix>
                <div class="formulate-input-suffix">
                  <span>{{ $t('global.time.minutes') }}</span>
                </div>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
      </FormulateForm>
    </b-card>
    <b-modal
      id="mc-modal-confirm"
      class="mc-modal"
      :title="$t('setting.list.trackerSensor.updateTimeLimit')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>{{ $t('global.updateConfirm') }}</p>
      </div>
      <template #modal-footer="{ cancel }">
        <McButton size="md" variant="secondary" :text="$t('global.action.no')" @click="cancel()" />
        <McButton
          size="md"
          variant="primary"
          :text="$t('global.action.yes')"
          @click="confirmForm"
        />
      </template>
    </b-modal>
  </Layout>
</template>
